'use client';

import { useEffect, useState } from 'react';
import { Check, FileText } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { BubbleActions, BubbleRows } from '@/components/chat/BubbleBlock';
import { saveFromChat } from '@/lib/chat/block-actions';
import { chatAttachmentUrl } from '@/lib/uploads/chat-attachment-url';
import { calendarDate, formatShortDate } from '@/lib/utils/date-format';
import {
  DIFFERENT_JOB_REQUEST,
  WHICH_JOB_REQUEST,
  receiptHeadline,
  receiptLinesRequest,
  type ReceiptLinesBlock,
} from '@/lib/chat/blocks/receipt-lines';

/**
 * A receipt photo read into lines, and one tap to put it on a job.
 *
 * The orange tap is a direct write to the job's own costs route
 * (`saveFromChat`), which checks `record_job_costs` itself. It reaches nobody:
 * a job cost is the business's own number, so there is no approval card. What
 * it posts is the block's `post` — the server's read — never anything typed.
 *
 * Which job is the owner's call. One candidate is shown as "Adding to …" with
 * Change; several are listed and nothing is picked until he taps one; none
 * means he says which, in words, and Mucka takes it from there.
 *
 * Once added, the strip becomes a receipt ("Added, with the photo") and stays
 * that way on a redraw in this browser, so a reloaded chat does not offer to
 * add the same trip twice.
 */

export interface ReceiptLinesProps {
  block: ReceiptLinesBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}

function pounds(n: number): string {
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function quantityLabel(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toLocaleString('en-GB', { maximumFractionDigits: 2 });
}

/** "Mon, 12 Oct", from YYYY-MM-DD, the same in every engine and zone (#41). */
function dayLabel(iso: string): string {
  return formatShortDate(calendarDate(iso));
}

const addedKey = (attachmentId: string) => `mucka:receipt-lines-added:${attachmentId}`;

function readAdded(attachmentId: string): string | null {
  try { return window.localStorage.getItem(addedKey(attachmentId)); } catch { return null; }
}
function rememberAdded(attachmentId: string, jobId: string): void {
  try { window.localStorage.setItem(addedKey(attachmentId), jobId); } catch { /* a convenience, not a record */ }
}

export function ReceiptLines({ block, live, onAnswer, onOpen }: ReceiptLinesProps) {
  const [jobId, setJobId] = useState<string | null>(block.preselectedJobId);
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addedTo, setAddedTo] = useState<string | null>(null);
  const [notForJob, setNotForJob] = useState(false);
  const [asked, setAsked] = useState(false);
  const [photoBroken, setPhotoBroken] = useState(false);

  // After mount, not in the initialiser: the server render has no storage.
  useEffect(() => { setAddedTo(readAdded(block.attachmentId)); }, [block.attachmentId]);

  const chosen = block.jobs.find(j => j.jobId === jobId) ?? null;
  const addedJob = addedTo ? block.jobs.find(j => j.jobId === addedTo) ?? null : null;
  const settled = !!addedTo || notForJob;
  const listing = !settled && block.canRecord && block.jobs.length > 1 && (!chosen || choosing);

  async function add() {
    if (!chosen) return;
    const request = receiptLinesRequest(block, chosen.jobId);
    if (!request) return;
    setBusy(true);
    setError(null);
    const out = await saveFromChat('POST', request.path, request.body);
    setBusy(false);
    if (!out.ok) {
      setError(out.message);
      return;
    }
    rememberAdded(block.attachmentId, chosen.jobId);
    setAddedTo(chosen.jobId);
  }

  function ask(text: string) {
    setAsked(true);
    onAnswer(text);
  }

  const notes: string[] = [];
  if (block.oneLine === 'disagree' && block.readLinesTotal != null && block.printedTotal != null) {
    notes.push(`The lines I read came to ${pounds(block.readLinesTotal)} but the receipt says ${pounds(block.printedTotal)}. You can split it up on the job.`);
  }
  if (block.totalsDisagree) {
    notes.push(`I read the total as ${pounds(block.totalsDisagree.first)} once and ${pounds(block.totalsDisagree.second)} the second time. Check it against the receipt.`);
  }

  return (
    <div className="mchat-rcpt" role="group" aria-label="Receipt">
      <div className="mchat-rcpt-head">
        {block.photo === 'image' && !photoBroken ? (
          // eslint-disable-next-line @next/next/no-img-element -- an authenticated app route, not a static asset
          <img
            className="mchat-rcpt-photo"
            src={chatAttachmentUrl(block.attachmentId)}
            alt="The receipt you sent"
            onError={() => setPhotoBroken(true)}
          />
        ) : (
          <span className="mchat-rcpt-file" aria-label="The receipt you sent">
            <Icon icon={FileText} size={20} />
          </span>
        )}
        <span className="mchat-rcpt-headline">
          <span className="mchat-rcpt-lead">{receiptHeadline(block)}</span>
          {(block.vendor || block.date) && (
            <span className="mchat-rcpt-meta">{[block.vendor, block.date ? dayLabel(block.date) : null].filter(Boolean).join(' · ')}</span>
          )}
        </span>
      </div>

      <div className="mchat-rcpt-lines" role="list" aria-label={`${block.rows.length + block.moreRows} lines`}>
        {block.rows.map((row, i) => (
          <div className="mchat-rcpt-line" role="listitem" key={`${i}:${row.description}`}>
            <span className="mchat-rcpt-desc">{row.description}</span>
            <span className="mchat-rcpt-qty">× {quantityLabel(row.quantity)}</span>
            <span className="mchat-rcpt-amount">{pounds(row.amount)}</span>
          </div>
        ))}
        {block.moreRows > 0 && (
          <div className="mchat-rcpt-line mchat-rcpt-line--more" role="listitem">
            {block.moreRows} more line{block.moreRows === 1 ? '' : 's'}, added with the rest
          </div>
        )}
      </div>

      <div className="mchat-rcpt-sums">
        <span className="mchat-rcpt-sum">
          {block.vat != null
            ? <><span>{block.vatIncluded ? 'Of which VAT' : 'VAT'}</span><span>{pounds(block.vat)}</span></>
            : <span>No VAT shown on the receipt</span>}
        </span>
        <span className="mchat-rcpt-sum mchat-rcpt-sum--total"><span>Total</span><span>{pounds(block.total)}</span></span>
      </div>

      {notes.map(n => <p className="mchat-rcpt-note" key={n}>{n}</p>)}

      {!block.canRecord && (
        <p className="mchat-rcpt-note">Your role can&apos;t put costs on a job, so there&apos;s nothing to add from here.</p>
      )}

      {block.canRecord && !notForJob && (
        <div className="mchat-rcpt-job">
          <span className="mchat-rcpt-label">{addedTo ? 'Added to' : listing ? 'Which job was it for?' : 'Adding to'}</span>
          {addedTo ? (
            <span className="mchat-rcpt-jobname">{addedJob?.label ?? 'the job'}</span>
          ) : listing ? null : chosen ? (
            <span className="mchat-rcpt-jobrow">
              <span className="mchat-rcpt-jobname">
                {chosen.label}
                <span className="mchat-rcpt-jobsub">as materials</span>
              </span>
              <button
                type="button"
                className="mchat-rcpt-change"
                disabled={!live || busy || asked}
                onClick={() => (block.jobs.length > 1 ? setChoosing(true) : ask(DIFFERENT_JOB_REQUEST))}
              >
                Change
              </button>
            </span>
          ) : (
            <span className="mchat-rcpt-jobname mchat-rcpt-jobname--none">I couldn&apos;t find a job on that day.</span>
          )}
        </div>
      )}

      {listing && (
        <div role="radiogroup" aria-label="Which job">
          {block.jobs.map(j => (
            <button
              key={j.jobId}
              type="button"
              role="radio"
              aria-checked={j.jobId === jobId}
              className={`mchat-row mchat-rcpt-pick${j.jobId === jobId ? ' mchat-rcpt-pick--on' : ''}`}
              disabled={!live || busy}
              onClick={() => { setJobId(j.jobId); setChoosing(false); setError(null); }}
            >
              <span className="mchat-rcpt-tick" aria-hidden="true">{j.jobId === jobId && <Icon icon={Check} size={13} />}</span>
              <span className="mchat-row-main"><span className="mchat-row-title">{j.label}</span></span>
            </button>
          ))}
          <button type="button" className="mchat-row" disabled={!live || busy || asked} onClick={() => ask(DIFFERENT_JOB_REQUEST)}>
            <span className="mchat-row-main"><span className="mchat-row-title mchat-rcpt-other">A different job</span></span>
          </button>
        </div>
      )}

      {block.canRecord && !settled && !chosen && block.jobs.length === 0 && (
        <button type="button" className="mchat-row" disabled={!live || asked} onClick={() => ask(WHICH_JOB_REQUEST)}>
          <span className="mchat-row-main"><span className="mchat-row-title mchat-rcpt-other">Tell Mucka which job</span></span>
        </button>
      )}

      {error && <p className="mchat-body mchat-error" role="alert">{error}</p>}

      {block.canRecord && (
        <BubbleActions
          done={addedTo ? 'Added, with the photo' : notForJob ? 'Not for a job, so nothing was recorded' : null}
          actions={[
            {
              label: error ? 'Try again' : `Add ${pounds(block.total)} to the job`,
              busyLabel: 'Adding…',
              busy,
              disabled: !live || !chosen || listing,
              onPress: () => { void add(); },
            },
            { label: 'Not for a job', tone: 'quiet', disabled: !live || busy, onPress: () => setNotForJob(true) },
          ]}
        />
      )}

      {addedTo && (
        <BubbleRows rows={[{ id: 'open-job', title: 'Open the job', onPress: () => onOpen(`/jobs/${addedTo}`) }]} />
      )}
    </div>
  );
}
